import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  X, MapPin, Calendar, Images, ChevronLeft, ChevronRight, ArrowLeft, Play, Film,
  ArrowRight, ChevronRight as Chevron, Mail, Handshake, Home as HomeIcon
} from 'lucide-react';
import { useFieldAlbums, useAlbumPhotos, FIELD_CATEGORIES, FieldAlbum } from '../hooks/useFieldAlbums';
import { thumb, IMG_PLACEHOLDER } from '../lib/imageUtils';
import { youtubeThumbnail } from '../lib/youtubeUtils';
import VideoLightbox from '../components/media/VideoLightbox';

const formatDate = (d?: string) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
const catLabel = (v: string) => FIELD_CATEGORIES.find((c) => c.value === v)?.label || v;

const typeBadge = (ct?: string): { label: string; icon: React.ReactNode } | null => {
  switch (ct) {
    case 'video': return { label: 'Vidéo', icon: <Play size={11} /> };
    case 'photo_video': return { label: 'Photo + Vidéo', icon: <Film size={11} /> };
    case 'photo': return { label: 'Photo', icon: <Images size={11} /> };
    case 'album': return { label: 'Album', icon: <Images size={11} /> };
    default: return null;
  }
};

const isVideoContent = (a: FieldAlbum) => (a.content_type === 'video' || a.content_type === 'photo_video') && !!a.video_id;

const cardThumb = (a: FieldAlbum): string => {
  if (a.content_type === 'video' || a.content_type === 'photo_video') {
    if (a.video_thumbnail_custom) return a.video_thumbnail_custom;
    if (a.video_id) return youtubeThumbnail(a.video_id, 'hq');
  }
  return a.cover_image ? thumb(a.cover_image, 700) : '';
};

const PHOTOS_INITIAL = 18;

/* ---------- Carte album réutilisable ---------- */
const AlbumCard: React.FC<{ album: FieldAlbum; onClick: () => void }> = ({ album, onClick }) => {
  const isVid = isVideoContent(album);
  const badge = typeBadge(album.content_type);
  const img = cardThumb(album);
  return (
    <button onClick={onClick}
      className="group flex flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white text-left transition-all duration-300 hover:-translate-y-1 hover:border-neutral-300 hover:shadow-[0_24px_60px_-24px_rgba(0,0,0,0.25)]">
      <div className="relative aspect-[4/3] overflow-hidden bg-neutral-100">
        {img ? (
          <img src={img} alt={album.title} loading="lazy" decoding="async"
            onError={(e) => { (e.target as HTMLImageElement).src = IMG_PLACEHOLDER; }}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-neutral-300"><Images size={40} /></div>
        )}
        {isVid && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/10">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 shadow-lg transition-transform duration-300 group-hover:scale-110">
              <Play size={24} className="ml-1 text-neutral-900" fill="currentColor" />
            </span>
          </div>
        )}
        {badge && (
          <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-neutral-950/85 px-2.5 py-1 text-[10px] font-bold text-white backdrop-blur">
            {badge.icon} {badge.label}
          </span>
        )}
        <span className="absolute left-3 top-3 rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-neutral-800 backdrop-blur">
          {catLabel(album.category)}
        </span>
        {!isVid && (album.photo_count ?? 0) > 0 && (
          <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full bg-neutral-950/85 px-2.5 py-1 text-[10px] font-bold text-white backdrop-blur">
            <Images size={11} /> {album.photo_count}
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-black text-neutral-900 leading-tight">{album.title}</h3>
        {album.short_description && <p className="mt-1.5 text-sm text-neutral-600 line-clamp-2">{album.short_description}</p>}
        <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-neutral-400">
          {album.event_date && <span className="inline-flex items-center gap-1"><Calendar size={12} /> {formatDate(album.event_date)}</span>}
          {album.location && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {album.location}</span>}
        </div>
      </div>
    </button>
  );
};

/* ---------- CTA final ---------- */
const FinalCTA: React.FC = () => (
  <section className="mt-16 overflow-hidden rounded-3xl bg-neutral-950 p-8 text-white sm:p-12">
    <div className="mx-auto max-w-2xl text-center">
      <h2 className="text-2xl font-black tracking-tight sm:text-3xl">Vous souhaitez collaborer avec Proxy Emobility ?</h2>
      <p className="mt-3 text-neutral-300">Partenaires, institutions, investisseurs ou clients : construisons ensemble la mobilité électrique de demain en Afrique de l'Ouest.</p>
      <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary-600 px-6 py-3 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-primary-500">
          <Mail size={16} /> Nous contacter
        </Link>
        <Link to="/proxy-fleet" className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/25 px-6 py-3 text-sm font-bold text-white transition-all hover:bg-white/10">
          <Handshake size={16} /> Devenir partenaire
        </Link>
      </div>
    </div>
  </section>
);

/* ---------- Template détail album ---------- */
const AlbumView: React.FC<{
  album: FieldAlbum;
  allAlbums: FieldAlbum[];
  onBack: () => void;
  onOpenAlbum: (a: FieldAlbum) => void;
}> = ({ album, allAlbums, onBack, onOpenAlbum }) => {
  const { photos, loading } = useAlbumPhotos(album.id);
  const [visible, setVisible] = useState(PHOTOS_INITIAL);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [showVideo, setShowVideo] = useState(false);
  const hasVideo = isVideoContent(album);

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); setVisible(PHOTOS_INITIAL); }, [album.id]);

  const shown = photos.slice(0, visible);

  const heroImg = album.cover_image
    ? thumb(album.cover_image, 1600)
    : (hasVideo ? youtubeThumbnail(album.video_id!, 'maxres') : (photos[0]?.image_url ? thumb(photos[0].image_url, 1600) : ''));

  const related = useMemo(() => {
    const others = allAlbums.filter((a) => a.id !== album.id);
    const sameCat = others.filter((a) => a.category === album.category);
    const rest = others.filter((a) => a.category !== album.category);
    return [...sameCat, ...rest].slice(0, 3);
  }, [allAlbums, album.id, album.category]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (lightbox === null) return;
      if (e.key === 'Escape') setLightbox(null);
      if (e.key === 'ArrowRight') setLightbox((i) => (i === null ? null : Math.min(photos.length - 1, i + 1)));
      if (e.key === 'ArrowLeft') setLightbox((i) => (i === null ? null : Math.max(0, i - 1)));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, photos.length]);

  const badge = typeBadge(album.content_type);

  return (
    <div>
      <nav className="mb-5 flex items-center gap-1.5 text-xs font-medium text-neutral-400">
        <Link to="/" className="inline-flex items-center gap-1 hover:text-neutral-700"><HomeIcon size={13} /> Accueil</Link>
        <Chevron size={13} />
        <button onClick={onBack} className="hover:text-neutral-700">Sur le terrain</button>
        <Chevron size={13} />
        <span className="truncate text-neutral-600">{album.title}</span>
      </nav>

      <header className="relative overflow-hidden rounded-3xl border border-neutral-200 bg-neutral-900">
        <div className="relative aspect-[16/10] sm:aspect-[21/9]">
          {heroImg ? (
            <img src={heroImg} alt={album.title} loading="eager" decoding="async"
              onError={(e) => { (e.target as HTMLImageElement).src = IMG_PLACEHOLDER; }}
              className="h-full w-full object-cover" />
          ) : (
            <div className="h-full w-full bg-gradient-to-br from-neutral-800 to-neutral-950" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/10" />

          {hasVideo && (
            <button onClick={() => setShowVideo(true)} className="group absolute inset-0 flex items-center justify-center" aria-label="Lire la vidéo">
              <span className="flex h-20 w-20 items-center justify-center rounded-full bg-white/95 shadow-2xl transition-transform duration-300 group-hover:scale-110">
                <Play size={34} className="ml-1.5 text-neutral-900" fill="currentColor" />
              </span>
            </button>
          )}

          <div className="absolute inset-x-0 bottom-0 p-6 sm:p-10">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-primary-600 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white">
                {catLabel(album.category)}
              </span>
              {badge && (
                <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-3 py-1 text-[11px] font-bold text-white backdrop-blur">
                  {badge.icon} {badge.label}
                </span>
              )}
            </div>
            <h1 className="mt-3 max-w-3xl text-3xl font-black leading-[1.05] tracking-tight text-white sm:text-4xl lg:text-5xl">{album.title}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-white/80">
              {album.event_date && <span className="inline-flex items-center gap-1.5"><Calendar size={15} /> {formatDate(album.event_date)}</span>}
              {album.location && <span className="inline-flex items-center gap-1.5"><MapPin size={15} /> {album.location}</span>}
              {!hasVideo && photos.length > 0 && <span className="inline-flex items-center gap-1.5"><Images size={15} /> {photos.length} photos</span>}
            </div>
            {album.short_description && <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/85 sm:text-base">{album.short_description}</p>}
          </div>
        </div>
      </header>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <button onClick={onBack} className="inline-flex items-center gap-2 rounded-xl border border-neutral-300 px-4 py-2.5 text-sm font-bold text-neutral-700 transition-colors hover:border-neutral-900">
          <ArrowLeft size={16} /> Tous les moments forts
        </button>
        <div className="flex flex-wrap gap-2">
          <Link to="/contact" className="inline-flex items-center gap-2 rounded-xl bg-neutral-900 px-4 py-2.5 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-primary-600">
            <Mail size={15} /> Nous contacter
          </Link>
          <Link to="/proxy-fleet" className="inline-flex items-center gap-2 rounded-xl border border-neutral-300 px-4 py-2.5 text-sm font-bold text-neutral-700 transition-colors hover:border-neutral-900">
            <Handshake size={15} /> Devenir partenaire
          </Link>
        </div>
      </div>

      {album.description && (
        <section className="mx-auto mt-12 max-w-3xl">
          <div className="mb-5 flex items-center gap-3">
            <span className="h-px w-8 bg-primary-500" />
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-primary-600">Le récit</span>
          </div>
          <div className="space-y-4 text-[15px] leading-[1.8] text-neutral-700 sm:text-base">
            {album.description.split('\n').filter((p) => p.trim()).map((para, i) => <p key={i}>{para}</p>)}
          </div>
        </section>
      )}

      {album.content_type !== 'video' && (
        <section className="mt-14">
          {(photos.length > 0 || loading) && (
            <div className="mb-6 flex items-center gap-3">
              <span className="h-px w-8 bg-primary-500" />
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-primary-600">Galerie</span>
            </div>
          )}

          {loading && (
            <div className="columns-2 gap-3 sm:columns-3 lg:columns-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="mb-3 break-inside-avoid animate-pulse rounded-xl bg-neutral-100" style={{ height: `${140 + (i % 3) * 50}px` }} />
              ))}
            </div>
          )}

          {!loading && photos.length === 0 && album.content_type !== 'photo_video' && (
            <p className="text-sm text-neutral-400">Les photos de cet album seront bientôt disponibles.</p>
          )}

          {!loading && photos.length > 0 && (
            <div className="columns-2 gap-3 sm:columns-3 lg:columns-4">
              {shown.map((photo, idx) => (
                <button key={photo.id} onClick={() => setLightbox(idx)}
                  className="group relative mb-3 block w-full break-inside-avoid overflow-hidden rounded-xl bg-neutral-100">
                  <img src={photo.thumbnail_url || thumb(photo.image_url, 600)} alt={photo.alt_text || album.title}
                    loading="lazy" decoding="async"
                    onError={(e) => { (e.target as HTMLImageElement).src = IMG_PLACEHOLDER; }}
                    className="w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <span className="absolute inset-0 bg-neutral-900/0 transition-colors duration-300 group-hover:bg-neutral-900/10" />
                </button>
              ))}
            </div>
          )}

          {visible < photos.length && (
            <div className="mt-8 text-center">
              <button onClick={() => setVisible((v) => v + 18)}
                className="rounded-xl border border-neutral-300 px-6 py-3 text-sm font-bold text-neutral-700 transition-colors hover:border-neutral-900">
                Charger plus de photos ({photos.length - visible} restantes)
              </button>
            </div>
          )}
        </section>
      )}

      {related.length > 0 && (
        <section className="mt-16">
          <div className="mb-6 flex items-end justify-between gap-4">
            <div>
              <div className="mb-2 flex items-center gap-3">
                <span className="h-px w-8 bg-primary-500" />
                <span className="text-xs font-bold uppercase tracking-[0.18em] text-primary-600">À découvrir</span>
              </div>
              <h2 className="text-2xl font-black tracking-tight text-neutral-900">Autres moments forts</h2>
            </div>
            <button onClick={onBack} className="hidden items-center gap-1.5 text-sm font-bold text-neutral-600 hover:text-neutral-900 sm:inline-flex">
              Tout voir <ArrowRight size={15} />
            </button>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((a) => <AlbumCard key={a.id} album={a} onClick={() => onOpenAlbum(a)} />)}
          </div>
        </section>
      )}

      <FinalCTA />

      {showVideo && album.video_id && <VideoLightbox videoId={album.video_id} title={album.title} onClose={() => setShowVideo(false)} />}

      {lightbox !== null && photos[lightbox] && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4" onClick={() => setLightbox(null)}>
          <button className="absolute right-4 top-4 text-white/80 hover:text-white" onClick={() => setLightbox(null)} aria-label="Fermer"><X size={28} /></button>
          {lightbox > 0 && (
            <button className="absolute left-2 sm:left-4 text-white/80 hover:text-white" onClick={(e) => { e.stopPropagation(); setLightbox(lightbox - 1); }} aria-label="Précédent"><ChevronLeft size={36} /></button>
          )}
          <figure className="max-h-[90vh] max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <img src={photos[lightbox].image_url} alt={photos[lightbox].alt_text || album.title} onError={(e) => { (e.target as HTMLImageElement).src = IMG_PLACEHOLDER; }} className="max-h-[80vh] w-auto rounded-lg object-contain" />
            {photos[lightbox].caption && <figcaption className="mt-3 text-center text-sm text-white/70">{photos[lightbox].caption}</figcaption>}
            <p className="mt-1 text-center text-xs text-white/40">{lightbox + 1} / {photos.length}</p>
          </figure>
          {lightbox < photos.length - 1 && (
            <button className="absolute right-2 sm:right-4 text-white/80 hover:text-white" onClick={(e) => { e.stopPropagation(); setLightbox(lightbox + 1); }} aria-label="Suivant"><ChevronRight size={36} /></button>
          )}
        </div>
      )}
    </div>
  );
};

/* ---------- Page principale ---------- */
const SurLeTerrain: React.FC = () => {
  const { albums, loading } = useFieldAlbums(false);
  const [filter, setFilter] = useState<string>('all');
  const [params, setParams] = useSearchParams();
  const [videoModal, setVideoModal] = useState<{ id: string; title: string } | null>(null);
  const activeAlbumKey = params.get('album');

  const activeAlbum = useMemo(
    () => albums.find((a) => a.slug === activeAlbumKey || a.id === activeAlbumKey) || null,
    [albums, activeAlbumKey]
  );

  const filtered = filter === 'all'
    ? albums
    : filter === 'videos'
      ? albums.filter((a) => a.category === 'videos' || isVideoContent(a))
      : albums.filter((a) => a.category === filter);

  const categoriesPresent = FIELD_CATEGORIES.filter((c) =>
    c.value === 'videos'
      ? albums.some((a) => a.category === 'videos' || isVideoContent(a))
      : albums.some((a) => a.category === c.value)
  );

  const openAlbum = (a: FieldAlbum) => {
    if (a.content_type === 'video' && isVideoContent(a)) { setVideoModal({ id: a.video_id!, title: a.title }); return; }
    params.set('album', a.slug || a.id); setParams(params);
  };
  const closeAlbum = () => { params.delete('album'); setParams(params); };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{activeAlbum ? `${activeAlbum.title} | Sur le terrain` : 'Sur le terrain'} | Proxy Emobility</title>
        <meta name="description" content={activeAlbum?.short_description || "Découvrez Proxy Emobility en action : événements, partenariats, formations et démonstrations de mobilité électrique en Afrique de l'Ouest."} />
        <link rel="canonical" href={`https://proxyemobility.com/sur-le-terrain${activeAlbum ? `?album=${activeAlbum.slug || activeAlbum.id}` : ''}`} />
      </Helmet>

      <div className="container mx-auto px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
        {activeAlbum ? (
          <AlbumView album={activeAlbum} allAlbums={albums} onBack={closeAlbum} onOpenAlbum={openAlbum} />
        ) : (
          <>
            <div className="mb-8">
              <span className="rounded-full bg-primary-50 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-primary-700">Proxy sur le terrain</span>
              <h1 className="mt-3 text-4xl font-black tracking-tight text-neutral-900 sm:text-5xl">Nos moments forts</h1>
              <p className="mt-3 max-w-2xl leading-relaxed text-neutral-600">
                La mémoire visuelle de Proxy Emobility : événements, partenariats institutionnels, formations et démonstrations à travers l'Afrique de l'Ouest.
              </p>
            </div>

            {categoriesPresent.length > 0 && (
              <div className="mb-8 flex flex-wrap gap-2">
                <button onClick={() => setFilter('all')}
                  className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${filter === 'all' ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}>
                  Tout
                </button>
                {categoriesPresent.map((c) => (
                  <button key={c.value} onClick={() => setFilter(c.value)}
                    className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${filter === c.value ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}>
                    {c.label}
                  </button>
                ))}
              </div>
            )}

            {loading && (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="overflow-hidden rounded-2xl border border-neutral-200">
                    <div className="aspect-[4/3] animate-pulse bg-neutral-100" />
                    <div className="space-y-2 p-4">
                      <div className="h-4 w-2/3 animate-pulse rounded bg-neutral-100" />
                      <div className="h-3 w-full animate-pulse rounded bg-neutral-100" />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!loading && albums.length === 0 && <p className="text-neutral-500">Les albums seront bientôt disponibles.</p>}

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((album) => <AlbumCard key={album.id} album={album} onClick={() => openAlbum(album)} />)}
            </div>

            {!loading && albums.length > 0 && <FinalCTA />}
          </>
        )}
      </div>
      {videoModal && <VideoLightbox videoId={videoModal.id} title={videoModal.title} onClose={() => setVideoModal(null)} />}
    </div>
  );
};

export default SurLeTerrain;
